import React, { forwardRef } from 'react';
import CustomDateInput from './CustomDateInput';
import { ClearButton } from 'shared/buttons';
import BaseLabel from '../../labels/BaseLabel';
import isNil from 'lodash/isNil';

/**
 * @typedef {React.ComponentPropsWithoutRef<CustomDateInput>} CustomDateInputProps
 *
 * @typedef {Object} AdditionalDateInputProps
 * @property {boolean} [clearButton={true}] Indicates if clear button is displayed after the input. True by default.
 *
 * @param {CustomDateInputProps & AdditionalDateInputProps} props
 * @param {React.Ref<HTMLDivElement>} ref
 * @returns {React.ReactElement}
 */
const BaseDateInput = ({ width, label, clearButton = true, ...props }, ref) => {
  const { onChange } = props;
  const onClear = () => {
    onChange({ value: null });
    if (ref && ref.current) {
      ref.current.focus();
    }
  };

  return (
    <span className='k-widget k-dateinput base-date-input' {...(!isNil(width) ? { style: { width } } : {})}>
      {label && <BaseLabel editorRef={ref}>{label}</BaseLabel>}
      <span className='k-dateinput-wrap'>
        <CustomDateInput ref={ref} {...props} />
        {clearButton && <ClearButton onClick={onClear} tabIndex={-1} />}
      </span>
    </span>
  );
};

/**
 * A base date input component.
 *
 * @param {CustomDateInputProps & AdditionalDateInputProps} props Component props. Extends CustomDateInput props.
 * @param {React.Ref<HTMLDivElement>} ref
 * @returns {React.ReactElement} Base date input component.
 */
const ForwardDateInput = forwardRef(BaseDateInput);

export default ForwardDateInput;
